import { z } from 'zod';

export const imageValidationSchema = z.object({
  file: z
    .instanceof(File)
    .refine((file) => file.type === 'image/jpeg' || file.type === 'image/png', {
      message: 'Chỉ chấp nhận file JPEG hoặc PNG',
    })
    .refine((file) => file.size <= 5 * 1024 * 1024, {
      message: 'Kích thước file tối đa 5MB',
    }),
});

export const formImageSchema = imageValidationSchema.extend({
  id: z.string().min(1),
  preview: z.string().min(1),
  state: z.enum(['pending', 'compressing', 'uploading', 'done', 'error']),
  error: z.string().optional(),
});

// --- API Request Schemas (snake_case) ---

export const componentValidationSchema = z.object({
  component_item_code: z.string().optional(),
  is_other_component: z.boolean(),
  other_component_description: z.string().optional(),
}).refine(
  (data) => {
    if (data.is_other_component) {
      return data.other_component_description && data.other_component_description.trim().length > 0;
    }
    return data.component_item_code && data.component_item_code.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn linh kiện từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['component_item_code'],
  }
);

export const defectValidationSchema = z.object({
  market_defect: z.string().optional(),
  is_other_defect: z.boolean(),
  other_defect_description: z.string().optional(),
}).refine(
  (data) => {
    if (data.is_other_defect) {
      return data.other_defect_description && data.other_defect_description.trim().length > 0;
    }
    return data.market_defect && data.market_defect.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn lỗi từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['market_defect'],
  }
);

export const causeValidationSchema = z.object({
  market_defect_cause: z.string().optional(),
  is_other_cause: z.boolean(),
  other_cause_description: z.string().optional(),
}).refine(
  (data) => {
    if (data.is_other_cause) {
      return data.other_cause_description && data.other_cause_description.trim().length > 0;
    }
    return data.market_defect_cause && data.market_defect_cause.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn nguyên nhân từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['market_defect_cause'],
  }
);

export const defectLineSchema = z.object({
  line_type: z.enum(['Machine', 'Component'], {
    errorMap: () => ({ message: 'Loại lỗi phải là Machine hoặc Component' }),
  }),
  component_item_code: z.string().optional(),
  is_other_component: z.boolean().optional(),
  other_component_description: z.string().optional(),
  market_defect: z.string().optional(),
  is_other_defect: z.boolean().optional(),
  other_defect_description: z.string().optional(),
  market_defect_cause: z.string().optional(),
  is_other_cause: z.boolean().optional(),
  other_cause_description: z.string().optional(),
  images: z.array(z.instanceof(File)).optional(),
}).refine(
  (data) => {
    if (data.line_type === 'Component') {
      if (data.is_other_component) {
        return data.other_component_description && data.other_component_description.trim().length > 0;
      }
      return data.component_item_code && data.component_item_code.trim().length > 0;
    }
    return true;
  },
  {
    message: 'Vui lòng chọn linh kiện từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['component_item_code'],
  }
).refine(
  (data) => {
    if (data.is_other_defect) {
      return data.other_defect_description && data.other_defect_description.trim().length > 0;
    }
    return data.market_defect && data.market_defect.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn lỗi từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['market_defect'],
  }
).refine(
  (data) => {
    if (data.is_other_cause) {
      return data.other_cause_description && data.other_cause_description.trim().length > 0;
    }
    return data.market_defect_cause && data.market_defect_cause.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn nguyên nhân từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['market_defect_cause'],
  }
);

// Full API request schema (snake_case) - used for API validation
export const ticketFormSchema = z.object({
  ticket_id_external: z.string().min(1, 'Mã sự cố không được để trống'),
  item_code: z.string().min(1, 'Mã sản phẩm không được để trống'),
  serial_no: z.string().min(1, 'Số serial không được để trống'),
  reported_date: z.string().min(1, 'Ngày báo cáo không được để trống'),
  technician_id: z.string().min(1, 'Mã kỹ thuật viên không được để trống'),
  technician_name: z.string().min(1, 'Tên kỹ thuật viên không được để trống'),
  customer_name: z.string().optional(),
  customer_phone: z.string().optional(),
  customer_address: z.string().optional(),
  notes: z.string().optional(),
  defect_lines: z.array(defectLineSchema).min(1, 'Phải có ít nhất 1 dòng lỗi'),
});

// --- Form Schemas (camelCase) ---

export const formComponentValidation = z.object({
  componentItemCode: z.string().optional(),
  isOtherComponent: z.boolean().optional(),
  otherComponentDescription: z.string().optional(),
}).refine(
  (data) => {
    if (data.isOtherComponent) {
      return data.otherComponentDescription && data.otherComponentDescription.trim().length > 0;
    }
    return data.componentItemCode && data.componentItemCode.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn linh kiện từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['componentItemCode'],
  }
);

export const formDefectValidation = z.object({
  marketDefect: z.string().optional(),
  isOtherDefect: z.boolean().optional(),
  otherDefectDescription: z.string().optional(),
}).refine(
  (data) => {
    if (data.isOtherDefect) {
      return data.otherDefectDescription && data.otherDefectDescription.trim().length > 0;
    }
    return data.marketDefect && data.marketDefect.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn lỗi từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['marketDefect'],
  }
);

export const formCauseValidation = z.object({
  marketDefectCause: z.string().optional(),
  isOtherCause: z.boolean().optional(),
  otherCauseDescription: z.string().optional(),
}).refine(
  (data) => {
    if (data.isOtherCause) {
      return data.otherCauseDescription && data.otherCauseDescription.trim().length > 0;
    }
    return data.marketDefectCause && data.marketDefectCause.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn nguyên nhân từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['marketDefectCause'],
  }
);

export const formDefectLineSchema = z.object({
  lineType: z.enum(['Machine', 'Component'], {
    errorMap: () => ({ message: 'Loại lỗi phải là Machine hoặc Component' }),
  }),
  componentItemCode: z.string().nullable().optional(),
  isOtherComponent: z.boolean().optional(),
  otherComponentDescription: z.string().optional(),
  marketDefect: z.string().nullable().optional(),
  isOtherDefect: z.boolean().optional(),
  otherDefectDescription: z.string().optional(),
  marketDefectCause: z.string().nullable().optional(),
  isOtherCause: z.boolean().optional(),
  otherCauseDescription: z.string().optional(),
  /** Internal: API defect line name (only set for lines loaded from server, not new ones) */
  apiDefectLineName: z.string().optional(),
  images: z.array(formImageSchema).optional(),
}).refine(
  (data) => {
    if (data.lineType === 'Component') {
      if (data.isOtherComponent) {
        return data.otherComponentDescription && data.otherComponentDescription.trim().length > 0;
      }
      return data.componentItemCode && data.componentItemCode.trim().length > 0;
    }
    return true;
  },
  {
    message: 'Vui lòng chọn linh kiện từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['componentItemCode'],
  }
).refine(
  (data) => {
    if (data.isOtherDefect) {
      return data.otherDefectDescription && data.otherDefectDescription.trim().length > 0;
    }
    return data.marketDefect && data.marketDefect.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn lỗi từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['marketDefect'],
  }
).refine(
  (data) => {
    if (data.isOtherCause) {
      return data.otherCauseDescription && data.otherCauseDescription.trim().length > 0;
    }
    return data.marketDefectCause && data.marketDefectCause.trim().length > 0;
  },
  {
    message: 'Vui lòng chọn nguyên nhân từ danh sách hoặc nhập mô tả tùy chỉnh',
    path: ['marketDefectCause'],
  }
);

// UI form schema (camelCase) - used for Create page validation
export const createTicketFormSchema = z.object({
  defectLines: z.array(formDefectLineSchema).min(1, 'Phải có ít nhất 1 dòng lỗi'),
  notes: z.string().max(500, 'Ghi chú tối đa 500 ký tự').optional(),
});

// Edit page form schema - allows empty defect lines (user can delete all lines)
export const editTicketFormSchema = z.object({
  defectLines: z.array(formDefectLineSchema),
  notes: z.string().max(500, 'Ghi chú tối đa 500 ký tự').optional(),
});

export type CreateTicketFormData = z.infer<typeof createTicketFormSchema>;
export type EditTicketFormData = z.infer<typeof editTicketFormSchema>;
